import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { NavGroup, NavLeaf } from "./nav";
import { searchNav, type SearchHit } from "./navSearch";

/**
 * The header's menu search (owner 2026-10-22), just before the namespace
 * switch: type what you want ("funds", "pin", "operator email") and pick the
 * screen. Each hit says where it sits in the menu and what it is for, so the
 * operator also learns the way there.
 */
export function MenuSearch({ groups, account, onSwitch }: { groups: NavGroup[]; account: NavLeaf[]; onSwitch: (ns: string) => void }) {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const hits = searchNav(groups, account, input);
  const open = (leaf: NavLeaf) => {
    setInput("");
    if (leaf.switchTo) onSwitch(leaf.switchTo);
    else if (leaf.external) window.open(leaf.to, "_blank", "noopener");
    else navigate(leaf.to);
  };
  return (
    <Autocomplete<SearchHit, false, false, false>
      options={hits}
      filterOptions={(o) => o}
      getOptionLabel={(h) => h.leaf.label}
      isOptionEqualToValue={(a, b) => a.leaf.to === b.leaf.to}
      inputValue={input}
      onInputChange={(_, v, reason) => setInput(reason === "reset" ? "" : v)}
      value={null}
      onChange={(_, h) => h && open(h.leaf)}
      blurOnSelect
      clearOnBlur={false}
      openOnFocus={false}
      noOptionsText={input.trim() ? "No screen matches. Try funds, pin, recharge, staff or password." : "Type what you want to open"}
      slotProps={{ paper: { sx: { width: 380 } }, popper: { placement: "bottom-end" } }}
      sx={{ width: { sm: 200, md: 280 }, display: { xs: "none", sm: "block" } }}
      renderInput={(params) => (
        <Box ref={params.InputProps.ref} sx={{ display: "flex", alignItems: "center", border: 1, borderColor: "divider", borderRadius: "6px", px: 1, py: 0.25 }}>
          <SearchIcon fontSize="small" sx={{ color: "text.disabled", mr: 0.75 }} />
          <InputBase
            inputProps={{ ...params.inputProps, "aria-label": "Search the menu" }}
            placeholder="Search: funds, pin, staff…"
            sx={{ fontSize: 14, flexGrow: 1, color: "inherit" }}
          />
        </Box>
      )}
      renderOption={(props, h) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <Box component="li" key={key} {...rest} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start !important" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}>
              {h.leaf.label}
              {h.leaf.external && <OpenInNewIcon sx={{ fontSize: 14, color: "text.disabled" }} />}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {h.where}
              {h.hint ? ` — ${h.hint}` : ""}
            </Typography>
          </Box>
        );
      }}
    />
  );
}
